/**
 * DataLineageExplainer — explainer for the DATA.LINEAGE Dual Pair Guarantee.
 *
 * DATA.LINEAGE pairs DATA.ORIGIN (D1, fibration) with DATA.REACH (D2,
 * opfibration) along the fibration ⊣ opfibration adjunction. Deploying
 * both sides is strictly stronger than deploying either alone: the
 * adjunction unit/counit witness that provenance reconstruction and
 * sensitivity propagation are mutual sections, so an origin-only or a
 * reach-only trace is structurally incomplete.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import {
  AdjunctionDiagram,
  CounterModelGrid,
  ExplainerCard,
  ResponsiveSplit,
  type ExplainerCrossLink,
  type ExplainerIdentity,
  type ExplainerTierContent,
} from "../shared/index.js";
import {
  COMPOSITIONALITY_PRINCIPLE_COLOR,
  DUAL_PAIR_COLOR,
  GRAY,
  THEOREM_COLOR,
  withAlpha,
} from "../../design-system/tokens.js";
import { MathInline, tex } from "../../components/math/index.js";

const COUNTER_MODELS = [
  {
    title: "D1 alone — origin without reach",
    body: "Provenance is reconstructable, but nothing constrains where a value may flow. A correctly-attributed secret still reaches a public sink: the origin story is real, the destination story is missing.",
  },
  {
    title: "D2 alone — reach without origin",
    body: "Sensitivity propagation is monotone, but the value's provenance is unconstrained. A laundered input flows only 'upward' yet carries a forged origin: the destination story is real, the source story is vacuous.",
  },
] as const;

const ACCENT = DUAL_PAIR_COLOR["DATA.LINEAGE"].color;

const IDENTITY: ExplainerIdentity = {
  id: "explainer-data-lineage",
  kind: "SSM · Dual Pair Guarantee (S1→S1)",
  title: "DATA.LINEAGE",
  subtitle: "D1 + D2 · fibration ⊣ opfibration",
  accent: ACCENT,
  accentAlt: COMPOSITIONALITY_PRINCIPLE_COLOR.TRACE.color,
};

const CROSS_LINKS: readonly ExplainerCrossLink[] = [
  {
    label: "DATA.ORIGIN (D1)",
    href: "#explainer-data-origin",
    accent: THEOREM_COLOR["DATA.ORIGIN"],
  },
  {
    label: "DATA.REACH (D2)",
    href: "#explainer-data-reach",
    accent: THEOREM_COLOR["DATA.REACH"],
  },
  {
    label: "TRACE (SDP dual)",
    href: "#explainer-trace",
    accent: COMPOSITIONALITY_PRINCIPLE_COLOR.TRACE.color,
  },
  { label: "Fibration", href: "#explainer-fibration" },
];

const TIERS: ExplainerTierContent = {
  tldr: (
    <p>
      <strong>DATA.LINEAGE</strong> says: every value knows where it came from <em>and</em> where it
      is allowed to go — and those two facts cohere. One side alone leaves a gap.
    </p>
  ),
  intuition: (
    <>
      <p>
        DATA.ORIGIN alone certifies provenance: you cannot misrepresent where a value came from.
        DATA.REACH alone certifies sensitivity: a value cannot flow somewhere below its level. But
        origin without reach lets a correctly-attributed secret still leak; reach without origin
        protects a value whose attribution was never real.
      </p>
      <p>
        DATA.LINEAGE is the joint guarantee, and it is <strong>strictly stronger</strong> than
        either side alone. The adjunction{" "}
        <MathInline>{tex`\mathrm{fibration} \dashv \mathrm{opfibration}`}</MathInline> certifies
        that cartesian reindexing (pull provenance back) and opcartesian pushforward (push
        sensitivity forward) are mutual sections — a complete, end-to-end lineage, by construction.
      </p>
    </>
  ),
  formal: (
    <>
      <p>
        For the lineage base <MathInline>{tex`B`}</MathInline> of provenance/sensitivity levels,
        DATA.LINEAGE pairs:
      </p>
      <ul>
        <li>
          <strong>D1.</strong> The provenance fibration{" "}
          <MathInline>{tex`\pi : E \to B`}</MathInline> with cartesian lift{" "}
          <MathInline>{tex`f^{*}`}</MathInline> — the right adjoint to substitution.
        </li>
        <li>
          <strong>D2.</strong> The sensitivity opfibration with opcartesian pushforward{" "}
          <MathInline>{tex`f_{+}`}</MathInline> — the left adjoint.
        </li>
      </ul>
      <p>
        The adjunction <MathInline>{tex`f_{+} \dashv f^{*}`}</MathInline> makes the round trip{" "}
        <MathInline>{tex`f^{*} \circ f_{+}`}</MathInline> a structural map, not a stipulated one:{" "}
        <strong>deploying both is strictly stronger than deploying either alone</strong>, because
        the unit and counit close the complementary gap each component leaves open. DATA.LINEAGE is
        a projection of the Categorical Immunity Theorem, not an independent story.
      </p>
      <AdjunctionDiagram
        leftCat="E↓ (sensitivity)"
        rightCat="E↑ (provenance)"
        leftAdjoint="f₊ (push)"
        rightAdjoint="f* (pull)"
        accent={ACCENT}
        ariaLabel="DATA.LINEAGE adjunction: opcartesian pushforward f₊ left-adjoint to cartesian reindex f* with lineage unit and counit"
      />
    </>
  ),
  relevance: (
    <>
      <p>
        <strong>Why "strictly stronger".</strong> Each component admits a counter-model the other
        rules out:
      </p>
      <CounterModelGrid counterModels={COUNTER_MODELS} ariaLabel="DATA.LINEAGE counter-models" />
      <p style={{ marginTop: "0.75rem" }}>
        Combining D1 and D2 along the adjunction closes both gaps in one envelope. The joint
        guarantee neutralizes end-to-end exfiltration: a path that is provenance-clean but
        reach-dirty (or vice versa) is <em>structurally inexpressible</em>.
      </p>
      <p>
        <strong>Theorem-collision check.</strong> Bypassing DATA.LINEAGE requires bypassing both
        DATA.ORIGIN and DATA.REACH, or escaping into a raw carrier on one side — both moves leave
        the kernel category and so vacate the guarantee rather than violate it. With its SDP dual{" "}
        <code>TRACE</code> it closes as the <code>ICG-Tr</code> Safety Envelope.
      </p>
    </>
  ),
  references: [
    {
      label: "Theorem Canon — Dual Pair Guarantees (§6)",
      path: "docs/reference/Theorem-Canon.md",
      note: "Section 6: Dual Pair Guarantees; the strict-strengthening claim.",
    },
    {
      label: "DATA.ORIGIN kernel module",
      path: "agda/Kernel/DATA/ORIGIN/Neutralization.agda",
    },
    {
      label: "DATA.REACH kernel module",
      path: "agda/Kernel/DATA/REACH/Neutralization.agda",
    },
  ],
};

const DIAGRAM = (
  <ResponsiveSplit
    ariaLabel="DATA.LINEAGE component theorems side by side"
    left={
      <div
        style={{
          padding: "0.5rem",
          backgroundColor: withAlpha(THEOREM_COLOR["DATA.ORIGIN"], "1A"),
          borderRadius: "0.375rem",
        }}
      >
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: THEOREM_COLOR["DATA.ORIGIN"],
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: "0.25rem",
          }}
        >
          D1 — Origin (right adjoint)
        </div>
        <code style={{ fontSize: "0.8125rem", color: GRAY[700] }}>f* : E_B → E_A</code>
      </div>
    }
    right={
      <div
        style={{
          padding: "0.5rem",
          backgroundColor: withAlpha(THEOREM_COLOR["DATA.REACH"], "1A"),
          borderRadius: "0.375rem",
        }}
      >
        <div
          style={{
            fontSize: "0.6875rem",
            fontWeight: 700,
            color: THEOREM_COLOR["DATA.REACH"],
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            marginBottom: "0.25rem",
          }}
        >
          D2 — Reach (left adjoint)
        </div>
        <code style={{ fontSize: "0.8125rem", color: GRAY[700] }}>f₊ : E_A → E_B</code>
      </div>
    }
  />
);

export function DataLineageExplainer(): React.JSX.Element {
  return (
    <ExplainerCard identity={IDENTITY} tiers={TIERS} diagram={DIAGRAM} crossLinks={CROSS_LINKS} />
  );
}
